import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ChatWithDataMessageRole,
  ChatWithDataStreamEvent,
  ChatWithDataStreamEventType,
} from '@recurrency/core-api-schema/dist/chatWithData/chatWithData';

import { globalAppStore } from 'hooks/useGlobalApp';

import { env } from 'utils/env';

import {
  clearRollingHistory,
  deleteConversation as deleteStoredConversation,
  getActiveId,
  listConversations,
  loadConversation,
  loadRollingHistory,
  MAX_STORED_CONVERSATIONS,
  saveConversation,
  saveRollingHistory,
  setActiveId,
} from './chatStorage';
import { ContentBlockType, ToolBlockStatus } from './types';
import type { ChatMessage, ChatWithDataFileData, ContentBlock, ConversationSummary, StoredConversation } from './types';

export type { ChatMessage } from './types';

export interface UseStreamingChatOptions {
  /**
   * Persist conversations to the multi-conversation localStorage store (with sidebar
   * history) and return the list. Default true. Used by the full-page chat.
   */
  persistHistory?: boolean;
  /**
   * Persist a single rolling conversation under this localStorage key (tenant-scoped),
   * keeping only the most recent exchanges, so history survives refresh and
   * close/reopen. For the page-aware drawer, which has no conversation sidebar.
   * Only used when `persistHistory` is false.
   */
  rollingHistoryKey?: string;
  /**
   * How many back-and-forths (user + assistant pairs) the rolling store keeps. Default 30.
   */
  rollingHistoryLimit?: number;
  /**
   * Provider to send question context (e.g. a description of the page the user is viewing).
   */
  getQuestionContext?: () => string;
}

/** Backend error when a replayed sessionId has expired server-side. */
const SESSION_NOT_FOUND = 'Session not found';

/** Debounce window for persisting so streamed tokens don't thrash localStorage. */
const PERSIST_DEBOUNCE_MS = 400;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/** First user message, truncated — used as the auto-derived conversation title. */
function deriveTitle(messages: ChatMessage[]): string {
  const firstUserMessage = messages.find((msg) => msg.role === ChatWithDataMessageRole.User);
  const text = firstUserMessage?.content?.trim() ?? '';
  if (!text) return 'New conversation';
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

function updateAssistantMsg(
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  id: string,
  updater: (msg: ChatMessage) => ChatMessage,
): void {
  setMessages((prev) => prev.map((msg) => (msg.id === id ? updater(msg) : msg)));
}

// ── Block helpers ──────────────────────────────────────────────────────

/** Append text to the last text block, or start a new one. */
function appendText(blocks: ContentBlock[], text: string): ContentBlock[] {
  const next = [...blocks];
  const last = next[next.length - 1];
  if (last?.type === ContentBlockType.Text) {
    next[next.length - 1] = { ...last, text: last.text + text };
  } else {
    next.push({ type: ContentBlockType.Text, text });
  }
  return next;
}

/** Mark the last running tool block as done. */
function completeLastTool(blocks: ContentBlock[]): ContentBlock[] {
  const next = [...blocks];
  for (let i = next.length - 1; i >= 0; i--) {
    const block = next[i];
    if (block.type === ContentBlockType.Tool && block.status === ToolBlockStatus.Running) {
      next[i] = { ...block, status: ToolBlockStatus.Done };
      break;
    }
  }
  return next;
}

/**
 * Read an SSE stream and call `onEvent` for each parsed `data: <json>` payload.
 * Uses a plain while-loop instead of async generators for esbuild compatibility.
 */
async function readSSE(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: ChatWithDataStreamEvent) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';

  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (result.value) {
      buffer += decoder.decode(result.value, { stream: true });
    }

    // SSE messages are separated by double newlines
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const rawMessage = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);

      for (const line of rawMessage.split('\n')) {
        if (line.startsWith('data: ')) {
          try {
            onEvent(JSON.parse(line.slice(6)));
          } catch {
            // skip malformed JSON
          }
        }
      }

      boundary = buffer.indexOf('\n\n');
    }
  }
}

/** Pick the conversation to open on mount: stored active → most recent → fresh. */
function resolveInitialConversation(): { id: string; conversation: StoredConversation | null } {
  const summaries = listConversations();
  const activeId = getActiveId();
  const chosenId = (activeId && summaries.some((s) => s.id === activeId) ? activeId : summaries[0]?.id) ?? null;
  if (!chosenId) return { id: generateId(), conversation: null };
  return { id: chosenId, conversation: loadConversation(chosenId) };
}

export function useStreamingChat({
  persistHistory = true,
  rollingHistoryKey,
  rollingHistoryLimit = 30,
  getQuestionContext,
}: UseStreamingChatOptions = {}) {
  const initial = useRef(
    persistHistory ? resolveInitialConversation() : { id: generateId(), conversation: null },
  ).current;

  // Rolling single-conversation store (drawer). Loaded once via a lazy initializer so
  // localStorage is read on mount, not on every render.
  const rolling = !persistHistory && !!rollingHistoryKey;
  const [rollingInitial] = useState(() => (rolling ? loadRollingHistory(rollingHistoryKey) : null));

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    if (initial.conversation?.messages) return initial.conversation.messages;
    return rollingInitial?.messages ?? [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>(() =>
    persistHistory ? listConversations() : [],
  );
  const [activeConversationId, setActiveConversationId] = useState<string>(initial.id);

  const sessionIdRef = useRef<string | undefined>(initial.conversation?.sessionId ?? rollingInitial?.sessionId);
  const createdAtRef = useRef<string>(initial.conversation?.createdAt ?? new Date().toISOString());
  const isTitleCustomRef = useRef<boolean>(initial.conversation?.isTitleCustom ?? false);
  const customTitleRef = useRef<string>(initial.conversation?.isTitleCustom ? initial.conversation.title : '');
  const activeIdRef = useRef<string>(initial.id);
  const abortRef = useRef<AbortController | undefined>();
  const isLoadingRef = useRef(false);
  // Skip the persist that a conversation-load (mount or select) triggers, so merely
  // opening a conversation doesn't bump its updatedAt — only new activity should.
  const skipNextPersistRef = useRef<boolean>(!!initial.conversation?.messages?.length);
  const getQuestionPrefixRef = useRef(getQuestionContext);
  getQuestionPrefixRef.current = getQuestionContext;

  // Persist on a debounce: message updates fire per streamed token, so writing on
  // every change would thrash localStorage. Refs (sessionId/title) are read at save
  // time — the `result` event sets them and also mutates messages, re-running this.
  useEffect(() => {
    if (!persistHistory || !messages.length) return undefined;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return undefined;
    }
    const timeout = setTimeout(() => {
      const title = isTitleCustomRef.current && customTitleRef.current ? customTitleRef.current : deriveTitle(messages);
      saveConversation({
        id: activeIdRef.current,
        sessionId: sessionIdRef.current,
        title,
        isTitleCustom: isTitleCustomRef.current,
        createdAt: createdAtRef.current,
        updatedAt: new Date().toISOString(),
        messages,
      });
      setConversations(listConversations());
    }, PERSIST_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [messages, persistHistory]);

  // Rolling persistence (drawer): debounced save of the current messages + sessionId to
  // a single key, capped to the most recent exchanges. sessionIdRef is read at save time
  // (the `result` event sets it and also mutates messages, re-running this). Clearing the
  // messages (Clear button / reset) removes the key so nothing lingers.
  useEffect(() => {
    if (!rolling) return undefined;
    if (!messages.length) {
      clearRollingHistory(rollingHistoryKey);
      return undefined;
    }
    const timeout = setTimeout(() => {
      saveRollingHistory(
        rollingHistoryKey,
        { sessionId: sessionIdRef.current, messages },
        // A back-and-forth is a user + assistant message.
        rollingHistoryLimit * 2,
      );
    }, PERSIST_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [messages, rolling, rollingHistoryKey, rollingHistoryLimit]);

  // Swap the active conversation's state in. All values are stable refs/setters.
  const loadInto = useCallback((id: string, conversation: StoredConversation | null) => {
    // Loading non-empty messages shouldn't re-persist; a fresh/empty one has nothing
    // to persist until its first message (which should persist).
    skipNextPersistRef.current = (conversation?.messages?.length ?? 0) > 0;
    setMessages(conversation?.messages ?? []);
    sessionIdRef.current = conversation?.sessionId;
    createdAtRef.current = conversation?.createdAt ?? new Date().toISOString();
    isTitleCustomRef.current = conversation?.isTitleCustom ?? false;
    customTitleRef.current = conversation?.isTitleCustom ? conversation.title : '';
    activeIdRef.current = id;
    setActiveConversationId(id);
    setActiveId(id);
  }, []);

  const stopInFlight = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = undefined;
    isLoadingRef.current = false;
    setIsLoading(false);
  }, []);

  // Abort, clear messages, and drop the session id (the drawer's Clear button). When a
  // rolling store is active, emptying the messages also clears its key (see the effect above).
  const reset = useCallback(() => {
    stopInFlight();
    setMessages([]);
    sessionIdRef.current = undefined;
  }, [stopInFlight]);

  const sendMessage = useCallback(async (text: string, fileData?: ChatWithDataFileData) => {
    const trimmed = text.trim();
    if (!trimmed || isLoadingRef.current) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: ChatWithDataMessageRole.User,
      content: trimmed,
      timestamp: new Date(),
      fileName: fileData?.fileName,
    };

    const assistantMessageId = generateId();
    const assistantPlaceholder: ChatMessage = {
      id: assistantMessageId,
      role: ChatWithDataMessageRole.Assistant,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
    setIsLoading(true);
    isLoadingRef.current = true;

    const abort = new AbortController();
    abortRef.current = abort;

    const questionPrefix = getQuestionPrefixRef.current?.() ?? '';
    const outboundQuestion = questionPrefix ? `${questionPrefix}\n\n${trimmed}` : trimmed;

    // One request + stream into the assistant placeholder. Returns whether the
    // replayed session had expired, so the caller can retry with a fresh session.
    const runChatRequest = async (sessionId: string | undefined): Promise<{ sessionExpired: boolean }> => {
      const { activeTenant, accessToken, activeErpRole, sessionId: appSessionId } = globalAppStore.state;
      const url = `${env.CORE_API_URL}/tenants/${activeTenant.id}/chat/data`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
        'x-session-id': appSessionId,
      };
      if (activeErpRole) {
        headers['x-current-role-name'] = activeErpRole.name;
        headers['x-current-role-id'] = activeErpRole.foreignId;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ question: outboundQuestion, sessionId, fileData }),
        signal: abort.signal,
      });

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        if (body.includes(SESSION_NOT_FOUND)) return { sessionExpired: true };
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      let sessionExpired = false;
      await readSSE(reader, (event) => {
        switch (event.type) {
          case ChatWithDataStreamEventType.TextDelta: {
            const delta = event.text ?? '';
            if (!delta) break;
            updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
              ...msg,
              blocks: appendText(msg.blocks ?? [], delta),
            }));
            break;
          }

          case ChatWithDataStreamEventType.ToolStart: {
            const { toolName } = event;
            if (toolName) {
              updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
                ...msg,
                activeToolName: toolName,
                blocks: [
                  ...(msg.blocks ?? []),
                  { type: ContentBlockType.Tool, name: toolName, status: ToolBlockStatus.Running },
                ],
              }));
            }

            break;
          }

          case ChatWithDataStreamEventType.ToolDone:
            updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
              ...msg,
              activeToolName: undefined,
              blocks: completeLastTool(msg.blocks ?? []),
            }));
            break;

          case ChatWithDataStreamEventType.Table:
            if (event.table) {
              updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
                ...msg,
                blocks: [...(msg.blocks ?? []), { type: ContentBlockType.Table, table: event.table! }],
              }));
            }
            break;

          case ChatWithDataStreamEventType.Chart:
            if (event.chart) {
              updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
                ...msg,
                blocks: [...(msg.blocks ?? []), { type: ContentBlockType.Chart, chart: event.chart! }],
              }));
            }
            break;

          case ChatWithDataStreamEventType.MinMaxProposal:
            if (event.minMaxProposal) {
              updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
                ...msg,
                blocks: [
                  ...(msg.blocks ?? []),
                  { type: ContentBlockType.MinMaxProposal, proposal: event.minMaxProposal! },
                ],
              }));
            }
            break;

          case ChatWithDataStreamEventType.Result: {
            const { data } = event;
            if (!data) break;
            // Expired replayed session — bail so the caller can retry fresh. Leave
            // the placeholder streaming; the retry resets and re-streams into it.
            if (!data.success && (data.errorMessage ?? '').includes(SESSION_NOT_FOUND)) {
              sessionExpired = true;
              break;
            }
            if (data.sessionId) {
              sessionIdRef.current = data.sessionId;
            }
            updateAssistantMsg(setMessages, assistantMessageId, (msg) => {
              let blocks = [...(msg.blocks ?? [])];

              if (!data.success) {
                blocks = appendText(blocks, data.errorMessage || 'Something went wrong. Please try again.');
              }

              return {
                ...msg,
                blocks,
                done: true,
                activeToolName: undefined,
              };
            });
            break;
          }

          default:
            break;
        }
      });

      return { sessionExpired };
    };

    try {
      let outcome = await runChatRequest(sessionIdRef.current);

      // Stale session: drop the id, reset the placeholder, and retry once fresh.
      if (outcome.sessionExpired) {
        sessionIdRef.current = undefined;
        updateAssistantMsg(setMessages, assistantMessageId, (msg) => ({
          ...msg,
          blocks: [],
          activeToolName: undefined,
        }));
        outcome = await runChatRequest(undefined);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      updateAssistantMsg(setMessages, assistantMessageId, (msg) => {
        let blocks = completeLastTool(msg.blocks ?? []);
        const errorText = `Something went wrong: ${error?.message || 'Unknown error'}. Please try again.`;
        blocks = appendText(blocks, errorText);
        return {
          ...msg,
          blocks,
          done: true,
          activeToolName: undefined,
        };
      });
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      abortRef.current = undefined;
    }
  }, []);

  const selectConversation = useCallback(
    (id: string) => {
      if (id === activeIdRef.current) return;
      stopInFlight();
      loadInto(id, loadConversation(id));
    },
    [loadInto, stopInFlight],
  );

  // At the storage cap, starting another conversation would push the oldest out, so it's
  // blocked until the user deletes one. The active conversation is only counted once it
  // has been persisted, so this can't lock out a chat that's already open and empty.
  const isAtConversationLimit = persistHistory && conversations.length >= MAX_STORED_CONVERSATIONS;

  const newConversation = useCallback(() => {
    if (isAtConversationLimit) return;
    stopInFlight();
    loadInto(generateId(), null); // not written to storage until the first message
  }, [isAtConversationLimit, loadInto, stopInFlight]);

  const deleteConversation = useCallback(
    (id: string) => {
      deleteStoredConversation(id);
      const remaining = listConversations();
      setConversations(remaining);
      if (id === activeIdRef.current) {
        if (remaining.length) {
          loadInto(remaining[0].id, loadConversation(remaining[0].id));
        } else {
          loadInto(generateId(), null);
        }
      }
    },
    [loadInto],
  );

  const renameConversation = useCallback((id: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const conv = loadConversation(id);
    if (conv) {
      // Preserve updatedAt (spread from conv) — a rename isn't new activity.
      saveConversation({ ...conv, title: trimmed, isTitleCustom: true });
    }
    if (id === activeIdRef.current) {
      isTitleCustomRef.current = true;
      customTitleRef.current = trimmed;
    }
    setConversations(listConversations());
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    reset,
    conversations,
    activeConversationId,
    isAtConversationLimit,
    maxConversations: MAX_STORED_CONVERSATIONS,
    selectConversation,
    newConversation,
    deleteConversation,
    renameConversation,
  };
}
